import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { ShieldCheck, Crown, ExternalLink, Sparkles } from "lucide-react";

const Settings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isPro = !!user?.is_pro;

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const manage = async () => {
    try {
      const res = await api.get("/billing/portal");
      window.location.assign(res.data.url);
    } catch (e) {
      toast.message("No customer portal for promo/simulation upgrades.");
      navigate("/pricing");
    }
  };

  return (
    <Layout title="Settings" subtitle="Account & plan">
      <div className="max-w-3xl space-y-6" data-testid="settings-page">
        <div className="border border-zinc-800 bg-zinc-900/30 rounded-2xl p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 mb-4">Account</p>
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-14 w-14 rounded-2xl object-cover border border-zinc-700" />
            ) : (
              <div className="h-14 w-14 rounded-2xl bg-zinc-800 flex items-center justify-center text-teal-300 font-heading font-bold text-xl">
                {user?.name?.[0]?.toUpperCase() || "?"}
              </div>
            )}
            <div>
              <p className="text-zinc-100 font-medium text-lg font-heading">{user?.name}</p>
              <p className="text-zinc-500 font-mono text-sm">{user?.email}</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950" data-testid="subscription-state">
              <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mb-1">Subscription</p>
              <p className="text-teal-300 font-heading font-bold text-lg flex items-center gap-2">
                {isPro && <Crown className="h-4 w-4" />}
                {isPro ? "Pro Member" : "Free Plan"}
              </p>
              <p className="text-xs text-zinc-600 mt-1">
                {isPro
                  ? `Active${user?.pro_source ? ` · via ${user.pro_source}` : ""}`
                  : "Billing via Paddle"}
              </p>
            </div>
            <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950 flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 text-teal-300 mt-0.5" />
              <div>
                <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mb-1">Terms Accepted</p>
                <p className="text-zinc-300 text-sm">{user?.accepted_terms_at ? fmtDate(user.accepted_terms_at) : "—"}</p>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            {isPro ? (
              <button
                onClick={manage}
                data-testid="manage-subscription-settings-btn"
                className="flex items-center gap-2 border border-zinc-700 text-zinc-200 rounded-xl px-5 py-2 text-sm hover:border-teal-500/40 hover:text-teal-300 transition-colors duration-200"
              >
                <ExternalLink className="h-4 w-4" /> Manage Subscription
              </button>
            ) : (
              <button
                onClick={() => navigate("/pricing")}
                data-testid="settings-upgrade-btn"
                className="flex items-center gap-2 bg-teal-400 text-zinc-950 font-semibold rounded-xl px-5 py-2 text-sm hover:bg-teal-300 transition-colors duration-200"
              >
                <Sparkles className="h-4 w-4" /> Upgrade to Pro
              </button>
            )}
            <button
              onClick={logout}
              data-testid="settings-logout-btn"
              className="border border-zinc-700 text-zinc-300 rounded-xl px-5 py-2 text-sm hover:border-red-500 hover:text-red-400 transition-colors duration-200"
            >
              Log out
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Settings;
